/**
 * MissionFeed — Vertical scrollable mission card feed
 *
 * Renders rich type-specific MissionCard components in a vertical stack.
 * No masonry layout, no insight card interleaving. Full-width cards.
 *
 * @see Figma_assets/iOS/Home@2x.png
 */

import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@ownyou/ui-design-system';
import type { Mission, MissionCardType } from '@ownyou/ui-components';
import {
  staggerContainerVariants,
  staggerItemVariants,
  getVariants,
} from '@/utils/animations';
import { MissionCard } from './MissionCard';
import { getMissionCardLifecycleState, type CardFirstMissionCard } from '@/lib/missions/lifecycle';
import { createMissionSpineRunId, recordMissionSpineMarker } from '@/lib/mission-spine/instrumentation';

export interface MissionFeedProps {
  missions: Mission[];
  filter: string;
  onMissionView?: (mission: Mission) => void;
  onMissionSave?: (mission: Mission) => void;
  onMissionDismiss?: (mission: Mission) => void;
  onMissionFeedback?: (missionId: string, sentiment: 'positive' | 'negative') => void;
  feedbackStates?: Map<string, 'positive' | 'negative'>;
  /** Sprint 17g-4: Prediction mode — set of expanded mission IDs */
  expandedMissions?: Set<string>;
  /** Sprint 17g-4: Called when user taps "Try me" on a prediction card */
  onTryMe?: (missionId: string) => void;
  /** Sprint 17g-4: Called when user taps "No way" on a prediction card */
  onNoWay?: (missionId: string) => void;
  className?: string;
}

export const CATEGORY_TYPE_MAP: Record<string, MissionCardType[]> = {
  savings: ['shopping', 'savings', 'consumables'],
  ikigai: ['content', 'people', 'travel', 'entertainment', 'food'],
  health: ['health'],
};

export function filterMissions(missions: Mission[], filter: string): Mission[] {
  if (filter === 'all') return missions;
  if (['new', 'in_progress', 'snoozed', 'done'].includes(filter)) {
    return missions.filter((m) => {
      const state = getMissionCardLifecycleState(m as unknown as CardFirstMissionCard);
      switch (filter) {
        case 'new':
          return state === 'suggested' || state === 'viewed';
        case 'in_progress':
          return state === 'accepted' || state === 'in_progress';
        case 'snoozed':
          return state === 'snoozed';
        case 'done':
          return state === 'completed';
        default:
          return true;
      }
    });
  }
  const allowedTypes = CATEGORY_TYPE_MAP[filter];
  if (!allowedTypes) return missions;
  return missions.filter((m) => allowedTypes.includes(m.type));
}

export function MissionFeed({
  missions,
  filter,
  onMissionView,
  onMissionSave,
  onMissionDismiss,
  onMissionFeedback,
  feedbackStates,
  expandedMissions,
  onTryMe,
  onNoWay,
  className,
}: MissionFeedProps) {
  const filtered = useMemo(
    () => filterMissions(missions, filter),
    [missions, filter],
  );

  useEffect(() => {
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('slice-02-today-chip'),
      hop: 'today_chip_filter',
      status: filtered.length > 0 ? 'success' : 'skipped',
      reasonCode: filter,
      counts: {
        rawCount: missions.length,
        visibleCount: filtered.length,
      },
    }, { log: false });
  }, [filter, filtered.length, missions.length]);

  const containerVariants = getVariants(staggerContainerVariants);
  const itemVariants = getVariants(staggerItemVariants);

  return (
    <motion.div
      data-testid="mission-feed"
      className={cn(
        'grid gap-4',
        'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        className,
      )}
      variants={containerVariants}
      initial="initial"
      animate="animate"
    >
      {filtered.map((mission) => (
        <motion.div key={mission.id} data-mission-id={mission.id} variants={itemVariants}>
          <MissionCard
            mission={mission}
            onView={() => onMissionView?.(mission)}
            onSave={() => onMissionSave?.(mission)}
            onDismiss={() => onMissionDismiss?.(mission)}
            onFeedbackPositive={() => onMissionFeedback?.(mission.id, 'positive')}
            onFeedbackNegative={() => onMissionFeedback?.(mission.id, 'negative')}
            feedbackState={feedbackStates?.get(mission.id) ?? 'none'}
            alignmentBoost={(mission as unknown as { alignmentBoost?: number }).alignmentBoost}
            isGrowthMission={(mission as unknown as { isGrowthMission?: boolean }).isGrowthMission}
            isStale={(mission as unknown as { isStale?: boolean }).isStale}
            prediction={(mission as unknown as { prediction?: string }).prediction}
            isExpanded={!(mission as unknown as { prediction?: string }).prediction || expandedMissions?.has(mission.id) || !onTryMe}
            onTryMe={onTryMe ? () => onTryMe(mission.id) : undefined}
            onNoWay={onNoWay ? () => onNoWay(mission.id) : undefined}
            alignedDimensions={(mission as unknown as { alignedDimensions?: string[] }).alignedDimensions}
          />
        </motion.div>
      ))}
    </motion.div>
  );
}
